package com.rajen.angrejidictionary.pages.admin

import androidx.compose.runtime.Composable
import com.rajen.angrejidictionary.presentation.viewmodel.NotificationsViewModel
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.rows
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.dom.TextInput

private val Maroon = Color.rgb(0x21, 0x00, 0x00)
private val Muted = Color.rgb(0xB6, 0x9B, 0x8C)
private val FieldBorder = Color.rgb(0xDD, 0xCC, 0xBB)
private val ErrorRed = Color.rgb(0xD3, 0x35, 0x3E)

@Composable
fun NotificationsTab(viewModel: NotificationsViewModel) {
    Column(
        Modifier.fillMaxWidth().maxWidth(560.px),
        verticalArrangement = Arrangement.spacedBy(1.cssRem)
    ) {
        Column {
            SpanText(
                "📢 Broadcast Notification",
                Modifier.fontSize(1.2.cssRem).fontWeight(FontWeight.Bold).color(Maroon)
            )
            SpanText(
                "Sends a push notification to every registered device. Covers Android and iOS — web push isn't supported.",
                Modifier.fontSize(0.85.cssRem).color(Muted).margin(top = 0.2.cssRem)
            )
        }

        viewModel.lastResult?.let { result ->
            Column(
                SuccessStyle.toModifier(),
                verticalArrangement = Arrangement.spacedBy(0.3.cssRem)
            ) {
                SpanText("✅ Broadcast sent", Modifier.fontWeight(FontWeight.Bold))
                SpanText("Delivered: ${result.successCount}   •   Failed: ${result.failureCount}")
                if (result.prunedTokens > 0) {
                    SpanText("Removed ${result.prunedTokens} dead token(s) from storage")
                }
            }
        }

        viewModel.sendError?.let { error ->
            Div(AdminErrorStyle.toAttrs()) { Text("❌ $error") }
        }

        // Title
        Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
            SpanText("Title", Modifier.fontSize(0.9.cssRem).fontWeight(FontWeight.Medium).color(Maroon))
            TextInput(
                value = viewModel.title,
                attrs = {
                    onInput { viewModel.onTitleChange(it.value) }
                    attr("placeholder", "e.g. New word added today!")
                    style {
                        width(100.percent)
                        padding(0.85.cssRem)
                        borderRadius(12.px)
                        border(
                            1.px, LineStyle.Solid,
                            if (viewModel.hasAttemptedSend && !viewModel.isTitleValid) ErrorRed else FieldBorder
                        )
                        fontSize(1.cssRem)
                        color(Maroon)
                        backgroundColor(rgb(255, 255, 255))
                        property("box-sizing", "border-box")
                        property("outline", "none")
                    }
                }
            )
            if (viewModel.hasAttemptedSend && !viewModel.isTitleValid) {
                SpanText("Title is required", Modifier.fontSize(12.px).color(ErrorRed))
            }
        }

        // Description
        Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
            SpanText(
                "Description (optional)",
                Modifier.fontSize(0.9.cssRem).fontWeight(FontWeight.Medium).color(Maroon)
            )
            TextArea(attrs = {
                value(viewModel.description)
                onInput { viewModel.onDescriptionChange(it.value) }
                rows(3)
                style {
                    width(100.percent)
                    padding(0.85.cssRem)
                    borderRadius(12.px)
                    border(1.px, LineStyle.Solid, FieldBorder)
                    fontSize(1.cssRem)
                    color(Maroon)
                    backgroundColor(rgb(255, 255, 255))
                    property("box-sizing", "border-box")
                    property("outline", "none")
                    property("resize", "vertical")
                }
            })
        }

        // Image URL
        Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
            SpanText(
                "Image URL (optional)",
                Modifier.fontSize(0.9.cssRem).fontWeight(FontWeight.Medium).color(Maroon)
            )
            TextInput(
                value = viewModel.imageUrl,
                attrs = {
                    onInput { viewModel.onImageUrlChange(it.value) }
                    attr("placeholder", "https://…")
                    style {
                        width(100.percent)
                        padding(0.85.cssRem)
                        borderRadius(12.px)
                        border(
                            1.px, LineStyle.Solid,
                            if (viewModel.hasAttemptedSend && !viewModel.isImageUrlValid) ErrorRed else FieldBorder
                        )
                        fontSize(1.cssRem)
                        color(Maroon)
                        backgroundColor(rgb(255, 255, 255))
                        property("box-sizing", "border-box")
                        property("outline", "none")
                    }
                }
            )
            if (viewModel.hasAttemptedSend && !viewModel.isImageUrlValid) {
                SpanText("Image URL must start with https://", Modifier.fontSize(12.px).color(ErrorRed))
            }
        }

        // Attach a word (optional) — deep-links the notification to that word's detail screen.
        Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
            SpanText(
                "Attach a word (optional)",
                Modifier.fontSize(0.9.cssRem).fontWeight(FontWeight.Medium).color(Maroon)
            )
            SpanText(
                "Tapping the notification opens this word's detail screen instead of just the app.",
                Modifier.fontSize(0.78.cssRem).color(Muted)
            )

            val selected = viewModel.selectedWord
            if (selected != null) {
                Row(
                    Modifier.fillMaxWidth()
                        .backgroundColor(Color.rgb(0xE8, 0xF5, 0xE9))
                        .borderRadius(10.px)
                        .padding(0.6.cssRem, 0.9.cssRem)
                        .gap(0.6.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        "🔗 ${selected.word.englishWord}",
                        Modifier.fontSize(0.9.cssRem).fontWeight(FontWeight.Bold)
                            .color(Color.rgb(0x2E, 0x7D, 0x32))
                    )
                    Div(
                        Modifier.padding(0.15.cssRem, 0.6.cssRem)
                            .backgroundColor(Colors.White)
                            .color(Color.rgb(0x2E, 0x7D, 0x32))
                            .border(1.px, LineStyle.Solid, Color.rgba(0x2E, 0x7D, 0x32, 0.3f))
                            .borderRadius(999.px)
                            .fontSize(0.75.cssRem)
                            .cursor(Cursor.Pointer)
                            .toAttrs { onClick { viewModel.clearSelectedWord() } }
                    ) { Text("✕ Change") }
                }
            } else {
                Row(Modifier.fillMaxWidth().gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    TextInput(
                        value = viewModel.wordQuery,
                        attrs = {
                            onInput { viewModel.onWordQueryChange(it.value) }
                            onKeyDown { event -> if (event.key == "Enter") viewModel.searchWord() }
                            attr("placeholder", "Search for a word…")
                            style {
                                property("flex", "1")
                                property("min-width", "120px")
                                padding(0.7.cssRem, 0.85.cssRem)
                                borderRadius(12.px)
                                border(1.px, LineStyle.Solid, FieldBorder)
                                fontSize(0.95.cssRem)
                                color(Maroon)
                                backgroundColor(rgb(255, 255, 255))
                                property("box-sizing", "border-box")
                                property("outline", "none")
                            }
                        }
                    )
                    Div(
                        Modifier.padding(0.6.cssRem, 1.cssRem)
                            .backgroundColor(Color.rgb(0x19, 0x76, 0xD2))
                            .color(Colors.White)
                            .borderRadius(10.px)
                            .fontSize(0.85.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .cursor(Cursor.Pointer)
                            .whiteSpace(WhiteSpace.NoWrap)
                            .toAttrs { onClick { viewModel.searchWord() } }
                    ) { Text(if (viewModel.isSearchingWord) "…" else "🔍 Search") }
                }

                viewModel.wordSearchError?.let { err ->
                    SpanText(err, Modifier.fontSize(0.8.cssRem).color(Muted).margin(top = 0.3.cssRem))
                }

                val results = viewModel.wordSearchResults
                if (!results.isNullOrEmpty()) {
                    Column(Modifier.fillMaxWidth().gap(0.35.cssRem).margin(top = 0.3.cssRem)) {
                        results.take(6).forEach { item ->
                            Div(
                                Modifier.fillMaxWidth()
                                    .backgroundColor(Colors.White)
                                    .border(1.px, LineStyle.Solid, FieldBorder)
                                    .borderRadius(10.px)
                                    .padding(0.55.cssRem, 0.8.cssRem)
                                    .color(Color.rgb(0x2E, 0x7D, 0x32))
                                    .cursor(Cursor.Pointer)
                                    .toAttrs { onClick { viewModel.selectWord(item) } }
                            ) {
                                Text(
                                    "${item.word.englishWord} — " +
                                            (item.word.meaningGujarati.firstOrNull() ?: "")
                                )
                            }
                        }
                    }
                }
            }
        }

        Div(
            AdminButtonStyle.toAttrs { onClick { viewModel.send() } }
        ) {
            Text(if (viewModel.isSending) "Sending…" else "📢 Send to All Devices")
        }
    }
}